import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useJobs, useTriggerJobSync, useDashboardMetrics, useUpdateJob, useDiscoverJobs } from '../hooks/useApi';
import { SearchInput } from '../components/ui/SearchInput';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
import {
  Briefcase,
  MapPin,
  Users,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  Search,
  Archive,
  RotateCcw,
  Radar,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { useToast } from '../contexts/ToastContext';
import { SkeletonTable } from '../components/ui/Skeleton';

// Map SmartRecruiters job status to human-readable labels
const SR_STATUS_LABELS: Record<string, string> = {
  FILLED: 'Position Filled',
  CANCELLED: 'Cancelled',
  ON_HOLD: 'On Hold',
  OFFER: 'Offer Extended',
  CREATED: 'Created',
  SOURCING: 'Sourcing',
};

export default function Jobs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  // Extract filter values from URL (default status to 'active' if not specified)
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || 'active';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  // Get counts for dropdown labels
  const { data: metricsData } = useDashboardMetrics();
  const activeCount = metricsData?.jobs?.active ?? 0;
  const inactiveCount = metricsData?.jobs?.inactive ?? 0;
  const totalCount = metricsData?.jobs?.total ?? 0;

  // Hook for reactivating/archiving jobs
  const updateJobMutation = useUpdateJob();
  const discoverMutation = useDiscoverJobs();

  // Check if viewing archived jobs
  const isViewingArchived = status === 'inactive';

  // Update URL params and reset page when filters change
  const updateFilters = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      // Reset page to 1 when filters change (unless we're just changing page)
      if (!('page' in updates)) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      updateFilters({ search: value });
    },
    [updateFilters]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      updateFilters({ status: value });
    },
    [updateFilters]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateFilters({ page: String(newPage) });
    },
    [updateFilters]
  );

  const { data, isLoading, isFetching, error, refetch } = useJobs({
    page,
    limit,
    search: search || undefined,
    status: status || undefined,
  });
  const syncMutation = useTriggerJobSync();

  const hasActiveFilters = search || (status && status !== 'active');

  const handleSync = async (jobId: number) => {
    try {
      await syncMutation.mutateAsync(jobId);
      showToast('Candidate sync queued successfully', 'success');
    } catch (err) {
      const error = err as Error;
      showToast(`Sync failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleReactivate = async (jobId: number) => {
    try {
      await updateJobMutation.mutateAsync({ id: jobId, data: { status: 'active' } });
      showToast('Job reactivated successfully', 'success');
    } catch (err) {
      const error = err as Error;
      showToast(`Reactivation failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleArchive = async (jobId: number) => {
    if (!window.confirm('Archive this job? It will be set to inactive status.')) return;
    try {
      await updateJobMutation.mutateAsync({ id: jobId, data: { status: 'inactive' } });
      showToast('Job archived successfully', 'success');
    } catch (err) {
      const error = err as Error;
      showToast(`Archive failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleDiscoverJobs = async () => {
    try {
      const result = await discoverMutation.mutateAsync();
      showToast(result.message, 'success');
    } catch (err) {
      const error = err as Error;
      showToast(`Discovery failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  if (error) {
    return (
      <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading jobs: {error.message}</p>
          <button
            onClick={() => refetch()}
            className="btn btn-primary mt-4"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isViewingArchived && (
            <Archive className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          )}
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {isViewingArchived ? 'Archived Jobs' : 'All Jobs'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {data?.pagination.total ?? 0} {isViewingArchived ? 'archived' : ''} jobs synced from SmartRecruiters
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDiscoverJobs}
            className="btn btn-primary"
            disabled={discoverMutation.isPending}
            title="Sync candidates from SmartRecruiters for all active jobs"
          >
            <Radar className={`mr-2 h-4 w-4 ${discoverMutation.isPending ? 'animate-spin' : ''}`} />
            {discoverMutation.isPending ? 'Discovering...' : 'Discover Jobs'}
          </button>
          <button
            onClick={() => refetch()}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Archived Jobs Warning Banner */}
      {isViewingArchived && (
        <Alert variant="warning" title="Viewing Archived Jobs">
          You are viewing jobs that have been deactivated. These jobs are hidden from the main list.
          Click "Reactivate" on any job to restore it to active status.
        </Alert>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-4">
          <div className="w-full sm:max-w-xs">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by title or department..."
              isLoading={isFetching}
            />
          </div>
          {/* Custom Status Dropdown with Counts */}
          <div className="inline-flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Status
            </label>
            <div className="relative">
              <select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="block rounded-lg border bg-white dark:bg-gray-800 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-blue-500 focus:ring-blue-500 dark:focus:ring-offset-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm pr-10"
              >
                <option value="active">Active ({activeCount})</option>
                <option disabled className="text-gray-400">───────────</option>
                <option value="inactive">Archived ({inactiveCount})</option>
                <option disabled className="text-gray-400">───────────</option>
                <option value="all">All Jobs ({totalCount})</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {hasActiveFilters && (
          <button
            onClick={() => setSearchParams(new URLSearchParams())}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Jobs Table */}
      <div className="card dark:bg-gray-800 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Job
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Applications
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Last Sync
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="p-0">
                  <SkeletonTable rows={5} columns={6} showHeader={false} />
                </td>
              </tr>
            ) : data?.data.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  {hasActiveFilters ? (
                    isViewingArchived ? (
                      <>
                        <Archive className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
                        <p className="mt-2 text-gray-500 dark:text-gray-400">
                          No archived jobs found
                        </p>
                        <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
                          Jobs are automatically archived when marked as Filled, Cancelled, or On Hold in SmartRecruiters
                        </p>
                        <button
                          onClick={() => handleStatusChange('active')}
                          className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          View active jobs
                        </button>
                      </>
                    ) : (
                      <>
                        <Search className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
                        <p className="mt-2 text-gray-500 dark:text-gray-400">
                          No jobs match your search criteria
                        </p>
                        <button
                          onClick={() => setSearchParams(new URLSearchParams())}
                          className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          Clear filters
                        </button>
                      </>
                    )
                  ) : (
                    <>
                      <Briefcase className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
                      <p className="mt-2 text-gray-500 dark:text-gray-400">No jobs found</p>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              data?.data.map((job) => {
                const isInactive = job.status === 'inactive';
                const deactivationReason = job.srStatus ? SR_STATUS_LABELS[job.srStatus] || job.srStatus : null;

                return (
                  <tr
                    key={job.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-750 ${isInactive ? 'bg-gray-50/50 dark:bg-gray-900/30' : ''}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                            isInactive ? 'bg-gray-100 dark:bg-gray-700' : 'bg-primary-100 dark:bg-primary-900/50'
                          }`}
                        >
                          {isInactive ? (
                            <Archive className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          ) : (
                            <Briefcase className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/jobs/${job.id}`)}
                              className={`font-medium text-left ${
                                isInactive
                                  ? 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                  : 'text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400'
                              }`}
                            >
                              {job.title}
                            </button>
                            {isInactive && (
                              <Badge variant="secondary" size="xs">
                                <Archive className="h-3 w-3 mr-1" />
                                Archived
                              </Badge>
                            )}
                            {job.passiveSourcingEnabled === true && (
                              <span className="bg-blue-500/20 text-blue-400 text-xs font-medium px-2 py-0.5 rounded-full">
                                Sourcing
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${isInactive ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>
                            {job.department || 'No department'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`flex items-center text-sm ${isInactive ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>
                        <MapPin className="mr-1.5 h-4 w-4" />
                        {job.location || 'Remote'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            job.status === 'active'
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {job.status === 'active' ? 'Active' : 'Archived'}
                        </span>
                        {/* Show deactivation reason for inactive jobs */}
                        {isInactive && deactivationReason && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {deactivationReason}
                          </p>
                        )}
                        {/* Show deactivation date (using updatedAt as proxy) */}
                        {isInactive && job.updatedAt && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            Deactivated {formatDistanceToNow(new Date(job.updatedAt), { addSuffix: true })}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`flex items-center text-sm ${isInactive ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                        <Users className={`mr-1.5 h-4 w-4 ${isInactive ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'}`} />
                        {job.applicationCount ?? 0}
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-sm ${isInactive ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>
                      {job.lastSyncedAt ? (
                        <span title={format(new Date(job.lastSyncedAt), 'PPpp')}>
                          {formatDistanceToNow(new Date(job.lastSyncedAt), {
                            addSuffix: true,
                          })}
                        </span>
                      ) : (
                        'Never'
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Reactivate button for inactive jobs */}
                        {isInactive && (
                          <button
                            onClick={() => handleReactivate(job.id)}
                            className="rounded px-2 py-1 text-xs font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950 hover:text-green-700 dark:hover:text-green-300 border border-green-200 dark:border-green-800"
                            title="Reactivate job"
                            aria-label="Reactivate this job"
                            disabled={updateJobMutation.isPending}
                          >
                            <RotateCcw className={`h-3 w-3 inline mr-1 ${updateJobMutation.isPending ? 'animate-spin' : ''}`} aria-hidden="true" />
                            Reactivate
                          </button>
                        )}
                        {/* Archive button for active jobs */}
                        {!isInactive && (
                          <button
                            onClick={() => handleArchive(job.id)}
                            className="rounded px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950 hover:text-amber-700 dark:hover:text-amber-300 border border-amber-200 dark:border-amber-800"
                            title="Archive job"
                            aria-label="Archive this job"
                            disabled={updateJobMutation.isPending}
                          >
                            <Archive className="h-3 w-3 inline mr-1" aria-hidden="true" />
                            Archive
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          className="rounded p-1 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300"
                          title="View details"
                          aria-label="View job details"
                        >
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleSync(job.id)}
                          className="rounded p-1 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={job.status === 'inactive' ? 'Cannot sync inactive job' : 'Sync candidates'}
                          aria-label="Sync candidates from SmartRecruiters"
                          disabled={syncMutation.isPending || job.status === 'inactive'}
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${
                              syncMutation.isPending ? 'animate-spin' : ''
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                        <a
                          href={`https://www.smartrecruiters.com/app/jobs/details/${job.srJobId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded p-1 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300"
                          title="View in SmartRecruiters"
                          aria-label="View job in SmartRecruiters"
                        >
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-3">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing{' '}
              <span className="font-medium">
                {(page - 1) * limit + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(page * limit, data.pagination.total)}
              </span>{' '}
              of <span className="font-medium">{data.pagination.total}</span>{' '}
              results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() =>
                  handlePageChange(Math.min(data.pagination.totalPages, page + 1))
                }
                disabled={page === data.pagination.totalPages}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
